import React, {useRef} from 'react';
import {Picture} from '../Picture.jsx';
import {TextColor, TextPosition} from '../text-modifiers';
import {Link} from '../Link.jsx';
import {TrackingContextAppend} from '../TrackingContext.jsx';
import {CfEntry} from '../CfPreviewContext.jsx';
import {Video} from '../Video.jsx';
import {Slider} from '../slider.js';
import {IconButton} from '@bonprix/design-system';
import {XRay} from '@teaser/scripts/components/XRay.jsx';

/**
 * @typedef {TeaserLinkModel} TopicCarouselTeaserModel
 * @property {string} entryId
 * @property {string} headline
 * @property {string} styleAttribute
 * @property {string} backgroundColor
 * @property {string} contentType
 * @property {ImageModel | null} backgroundImage
 * @property {VideoModel | null} video
 */

/**
 * @typedef {TopicCarouselModel} TopicCarouselModel
 * @property {string} entryId
 * @property {'TOPIC_CAROUSEL'} type
 * @property {string} headline
 * @property {string} subline
 * @property {string} contentType
 * @property {string} labelBackgroundColor
 * @property {TextColor} labelTextColor
 * @property {TextPosition} labelAlignment
 * @property {TextPosition} headlineAndSublineAlignment
 * @property {TopicCarouselTeaserModel[]} teasers
 */

/**
 * @param {import('./ArticleOverviewTeaserContent.jsx').Device} Device
 */

/**
 * @param {TopicCarouselModel} model
 * @returns {JSX.Element}
 */
export function TopicCarousel({model}) {
    const slider = useRef(new Slider());

    return (
        <CfEntry entryId={model.entryId} contentType={model.contentType}>
            <article className="content-platform__teaser content-platform__topic-carousel">
                <XRay fieldId="internalName" />
                <div
                    className={`content-platform__topic-carousel__header ${TextPosition[model.headlineAndSublineAlignment]}`}
                >
                    {model.headline && (
                        <p className="content-platform__topic-carousel__headline">
                            {model.headline}
                            <XRay fieldId="headline" />
                        </p>
                    )}
                    {model.subline && (
                        <p className="content-platform__topic-carousel__subline">
                            {model.subline}
                            <XRay fieldId="subline" />
                        </p>
                    )}
                </div>
                <div className="content-platform__topic-carousel__slider-container">
                    <div ref={slider.current.setElement} className="content-platform__topic-carousel__slider -custom">
                        {model.teasers.map(
                            (teaser, index) =>
                                teaser &&
                                teaser.entryId && (
                                    <CfEntry
                                        key={teaser.entryId}
                                        entryId={teaser.entryId}
                                        contentType={teaser.contentType}
                                    >
                                        <div
                                            key={`${teaser.entryId}-${index}`}
                                            className="content-platform__teaser__container content-platform__topic-carousel__slide"
                                            style={{backgroundColor: teaser.backgroundColor}}
                                        >
                                            <XRay fieldId="internalName" />
                                            <TopicCarouselItem model={teaser} parent={model} />
                                        </div>
                                    </CfEntry>
                                ),
                        )}
                    </div>
                    <div className="content-platform__topic-carousel__button -prev">
                        <IconButton
                            type="button"
                            icon="arrow-left"
                            aria-label="Previous Slide"
                            onClick={(event) => {
                                event.stopPropagation();
                                slider.current.slideLeft(3);
                            }}
                        />
                    </div>
                    <div className="content-platform__topic-carousel__button -next">
                        <IconButton
                            type="button"
                            icon="arrow-right"
                            aria-label="Next Slide"
                            onClick={(event) => {
                                event.stopPropagation();
                                slider.current.slideRight(3);
                            }}
                        />
                    </div>
                </div>
            </article>
        </CfEntry>
    );
}

/**
 * @param {TopicCarouselTeaserModel} model
 * @param {TopicCarouselModel} parent
 * @returns {JSX.Element}
 */
function TopicCarouselItem({model, parent}) {
    return (
        <TrackingContextAppend value={{id: model.entryId}}>
            <Link key={model.entryId} model={model}>
                <Video model={model} aspectRatio={0.714}>
                    {model.backgroundImage ? (
                        <Picture
                            model={model.backgroundImage}
                            xxlarge={566}
                            xlarge={470}
                            large={350}
                            medium={575}
                            small={359}
                            aspectRatio={0.714}
                        />
                    ) : (
                        <div className="content-platform__teaser__spacer" />
                    )}
                </Video>
                {model.headline && (
                    <div className="content-platform__teaser__content">
                        <div
                            className={`content-platform__topic-carousel__slide-main 
                                                ${TextPosition[parent.labelAlignment]}`}
                        >
                            <p
                                className={`content-platform__topic-carousel__slide-headline 
                                                ${TextColor[parent.labelTextColor]} 
                                                -bg${TextColor[parent.labelBackgroundColor]}`}
                            >
                                {model.headline}
                                <XRay fieldId="headline" />
                            </p>
                        </div>
                    </div>
                )}
            </Link>
        </TrackingContextAppend>
    );
}
